import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Leaf, CheckCircle2, Loader2, AlertCircle } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const params = new URLSearchParams(window.location.search);
    const provider = params.get("provider");
    const sid = provider === "paypal" ? params.get("subscription_id") : params.get("session_id");
    const statusUrl = provider === "paypal" ? `/paypal/status/${sid}` : `/payments/status/${sid}`;
    if (!sid) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(statusUrl);
        if (data.payment_status === "paid") {
          await checkAuth();
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("error"); return; }
      } catch { /* keep polling */ }
      if (attempts >= 10) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background nutri-grain px-4">
      <div className="bg-card border rounded-2xl p-10 max-w-md w-full text-center space-y-5 shadow-sm">
        <div className="flex items-center justify-center gap-2 text-primary">
          <Leaf className="h-6 w-6" /> <span className="font-heading text-xl font-semibold">DietFlix</span>
        </div>
        {status === "checking" && (<>
          <Loader2 className="h-12 w-12 text-primary mx-auto animate-spin" />
          <h1 className="font-heading text-2xl font-semibold">Conferma del pagamento…</h1>
          <p className="text-muted-foreground">Stiamo attivando il tuo abbonamento, un attimo.</p>
        </>)}
        {status === "paid" && (<>
          <CheckCircle2 className="h-14 w-14 text-primary mx-auto" data-testid="payment-success-icon" />
          <h1 className="font-heading text-2xl font-semibold">Abbonamento attivo!</h1>
          <p className="text-muted-foreground">Grazie! Ora hai accesso a tutte le funzioni Premium.</p>
          <Button className="w-full" onClick={() => navigate("/dashboard")} data-testid="go-dashboard-button">Vai alla dashboard</Button>
        </>)}
        {(status === "timeout" || status === "error") && (<>
          <AlertCircle className="h-14 w-14 text-accent mx-auto" />
          <h1 className="font-heading text-2xl font-semibold">Verifica in corso</h1>
          <p className="text-muted-foreground">Il pagamento potrebbe richiedere qualche istante. Controlla lo stato nella sezione Abbonamento.</p>
          <Button className="w-full" onClick={() => navigate("/dashboard")}>Torna alla dashboard</Button>
        </>)}
      </div>
    </div>
  );
}
